import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { INameId } from 'constants/globalTypes'
import { IIngredientsModal } from 'pages/Food/IngredientsModal'
import { IMealSupplierModalState } from 'pages/Food/MealSuppliersModal'
import { IMealTypeModal } from 'pages/Food/MealTypeModal'
import { IAddFoodItemModalState } from 'pages/Food/FoodItems/AddFoodItemModal'
import { toast } from 'react-toastify'
import {
  IGenerateFoodReportRequest,
  ICreateDishRequest,
  ICreateIngredientRequest,
  ICreateMealSupplierRequest,
  ICreateMealTypeRequest,
  ICreateUpdateFoodInventoryItemRequest,
  IDishSingleResponse,
  IFoodCategorySingleResponse,
  IFoodInventoyItemSingleResponse,
  IGetAllDishesSingleResponse,
  IGetFoodReportResponse,
  IIngredientSingleResponse,
  IMealSupplierSingleResponse,
  IFoodReport,
  IEditFoodReport,
  IGetSelectedDishesReponse,
  IPostSelectedDishRequest,
  IGetFoodCompanySettingsResponse,
  IAddCompanyFoodSettingsRequest,
  IGetAllMealTypesSingleResponse,
  IGetAllFoodEligibleEmployeesSingleResponse,
  IAddFoodEmployeeSettingsRequest,
  IAddAdHocFoodProvisioningRequest,
  IAddStandByProvisioningRequest,
  IGetAllFoodProvisioningRecordsSingleResponse,
  ICreateFoodDietaryTagRequest,
  IUpdateFoodDietaryTagRequest,
  IFoodDietaryTagResponse,
  ICreateFoodKitchenRequest,
  IUpdateFoodKitchenRequest,
  IFoodKitchenResponse,
  ICreateFoodMealWindowRequest,
  IUpdateFoodMealWindowRequest,
  IFoodMealWindowResponse,
  ICreateFoodCatchmentRuleRequest,
  IUpdateFoodCatchmentRuleRequest,
  IFoodCatchmentRuleResponse,
  IEmployeeFoodProfile,
  IUpsertEmployeeFoodProfileRequest,
  IFoodConfigVersion,
  IUpsertFoodSitePolicyRequest,
  IGenerateFoodIntentsRequest,
  IGenerateFoodIntentsResponse,
  INextDayPreviewRow,
  IFoodCutoffRequest,
  IFoodAttendanceReconcileRequest,
  IFoodQueuedJobResponse,
} from 'types/api/foodApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IDishesModal, IFoodSlice } from 'types/food.interface'
import { baseUrl } from 'utils/axios'
import { formatParams, getParams, handleErrors } from 'utils/helpers'
import { ISelectedDish } from 'pages/Food/SelectedDishes/AddSelectedDishModal'
import { IFoodCompanySettingsModalState } from 'pages/Food/FoodCompanySettingsModal'
import { IFoodEmployeeSettingsModalState } from 'pages/Food/FoodEmployeeSettingsModal'
import { IAdHocFoodState } from 'pages/Food/ProvisioningRecords/AdHocFoodProvisioning'
import { IStandByFoodProvisioningState } from 'pages/Food/ProvisioningRecords/StandByFoodProvisioning'

const initialState: IFoodSlice = {
  isLoading: false,
  foodReport: null,
  oldFoodReport: null,
  selectedDishes: null,
  companySettings: null,
  provisioningRecords: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  eligibleEmployees: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  dishes: {
    data: [],
    singleDish: null,
    totalPages: 0,
    totalCount: 0,
  },
  mealTypes: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  mealSuppliers: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  mealIngredients: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  categories: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  items: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  dietaryTags: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  logisticsKitchens: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  mealWindows: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  catchmentRules: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  employeeFoodProfiles: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  selectedEmployeeFoodProfile: null,
  configVersions: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  nextDayPreview: [],
}

export const getFoodReport = createAsyncThunk('food/getFoodReport', async (date: any, thunkAPI) => {
  try {
    const resp: AxiosResponse<IGetFoodReportResponse> = await baseUrl.get(
      'food/management/report',
      {
        params: { date: new Date(date).toISOString() },
      },
    )

    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const generateFoodReport = createAsyncThunk(
  'food/generateFoodReport',
  async (data: IGenerateFoodReportRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodReport> = await baseUrl.post(
        'food/management/report/generate',
        data,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editFoodReport = createAsyncThunk(
  'food/editFoodReport',
  async ({ report, id }: { report: IEditFoodReport; id: number }, thunkAPI) => {
    try {
      const resp: AxiosResponse<any> = await baseUrl.patch(
        `food/management/report/${id}/edit`,
        report,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const confirmFoodReport = createAsyncThunk(
  'food/confirmFoodReport',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.patch(`food/management/report/${id}/confirm`)
      return null
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createMealType = createAsyncThunk(
  'food/createMealType',
  async (req: ICreateMealTypeRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<INameId> = await baseUrl.post('food/management/meal/type', req)
      await thunkAPI.dispatch(getMealTypes(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editMealType = createAsyncThunk(
  'food/editMealType',
  async (state: IMealTypeModal, thunkAPI) => {
    try {
      const { id } = state
      delete state.id
      const resp: AxiosResponse<INameId> = await baseUrl.patch('food/management/meal/type/' + id, {
        ...state,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteMealType = createAsyncThunk(
  'food/deleteMealType',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/meal/type/' + id)
      await thunkAPI.dispatch(getMealTypes(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createMealSupplier = createAsyncThunk(
  'food/createSupplier',
  async (state: IMealSupplierModalState, thunkAPI) => {
    try {
      const req: ICreateMealSupplierRequest = {
        ...state,
        dietaryPreferenceTypes: state.dietaryPreferenceTypes.map((i) => i.value),
      }
      const resp: AxiosResponse<IMealSupplierSingleResponse> = await baseUrl.post(
        'food/management/meal/supplier',
        req,
      )

      await thunkAPI.dispatch(getMealSuppliers(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editMealSupplier = createAsyncThunk(
  'food/editSupplier',
  async (state: IMealSupplierModalState, thunkAPI) => {
    try {
      const { id } = state
      delete state.id
      const req: ICreateMealSupplierRequest = {
        ...state,
        dietaryPreferenceTypes: state.dietaryPreferenceTypes.map((i) => i.value),
      }
      const resp: AxiosResponse<Omit<IMealSupplierSingleResponse, 'id'>> = await baseUrl.patch(
        'food/management/meal/supplier/' + id,
        req,
      )
      return { ...resp.data, id }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteMealSupplier = createAsyncThunk(
  'food/deleteMealSupplier',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/meal/supplier/' + id)

      await thunkAPI.dispatch(getMealSuppliers(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createIngredient = createAsyncThunk(
  'food/createIngredient',
  async (state: IIngredientsModal, thunkAPI) => {
    try {
      const req: ICreateIngredientRequest = {
        name: state.name,
        unitId: state.unit.value,
      }
      const resp: AxiosResponse<IIngredientSingleResponse> = await baseUrl.post(
        'food/management/ingredient',
        req,
      )

      await thunkAPI.dispatch(getMealIngredients(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editIngredient = createAsyncThunk(
  'food/editIngredient',
  async (state: IIngredientsModal, thunkAPI) => {
    try {
      const req: ICreateIngredientRequest = {
        name: state.name,
        unitId: state.unit.value,
      }
      const resp: AxiosResponse<IIngredientSingleResponse> = await baseUrl.patch(
        'food/management/ingredient/' + state.id,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteIngredient = createAsyncThunk(
  'food/deleteIngredient',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/ingredient/' + id)

      await thunkAPI.dispatch(getMealIngredients(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createDish = createAsyncThunk(
  'food/createDish',
  async (state: IDishesModal, thunkAPI) => {
    try {
      const req: ICreateDishRequest = {
        name: state.name,
        ingredients: state.ingredients.map((i) => ({
          ingredientId: i.ingredient.value,
          amount: i.amount,
        })),
        supplierId: state.supplier.value,
        mealTypeId: state.mealType.value,
        dietaryTagCodes: state.dietaryTagCodes,
      }
      const resp: AxiosResponse<IDishSingleResponse> = await baseUrl.post(
        'food/management/dish',
        req,
      )

      await thunkAPI.dispatch(getDishes(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editDish = createAsyncThunk('food/editDish', async (state: IDishesModal, thunkAPI) => {
  try {
    const req: ICreateDishRequest = {
      name: state.name,
      ingredients: state.ingredients.map((i) => ({
        ingredientId: i.ingredient.value,
        amount: i.amount,
      })),
      supplierId: state.supplier.value,
      mealTypeId: state.mealType.value,
      dietaryTagCodes: state.dietaryTagCodes,
    }
    const resp: AxiosResponse<IDishSingleResponse> = await baseUrl.patch(
      'food/management/dish/' + state.id,
      req,
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const deleteDish = createAsyncThunk('food/deleteDish', async (id: number, thunkAPI) => {
  try {
    await baseUrl.delete('food/management/dish/' + id)

    await thunkAPI.dispatch(getDishes(getParams({ location })))
    return id
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getMealTypes = createAsyncThunk(
  'food/getAllMealTypes',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllMealTypesSingleResponse>> =
        await baseUrl.get('food/management/meal/type', {
          params,
        })

      return {
        ...resp.data,
        data: resp.data.data.map((mt) => ({
          ...mt,
          serviceWindowEnd: mt.serviceWindowEnd.slice(0, 5),
          serviceWindowStart: mt.serviceWindowStart.slice(0, 5),
          planningCutOffTime: mt.planningCutOffTime.slice(0, 5),
        })),
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getDishes = createAsyncThunk('food/getAllDishes', async (params: any, thunkAPI) => {
  try {
    params = formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IGetAllDishesSingleResponse>> = await baseUrl.get(
      'food/management/dish',
      {
        params,
      },
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getProvisioningRecords = createAsyncThunk(
  'food/getProvisioningRecords',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllFoodProvisioningRecordsSingleResponse>> =
        await baseUrl.get('food/management/meal/provisioning-records/grouped', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleDish = createAsyncThunk(
  'food/getSingleDish',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IDishSingleResponse> = await baseUrl.get(
        'food/management/dish/' + id,
      )
      const values: IDishesModal = {
        id: resp.data.id,
        name: resp.data.name,
        mealType: resp.data.mealType
          ? { label: resp.data.mealType.name, value: resp.data.mealType.id }
          : null,
        dietaryTagCodes: (resp.data.dietaryTags || []).map((t) => t.code),
        ingredients:
          resp.data.ingredients?.map((i) => ({
            amount: i.amount,
            ingredient: {
              unit: i.ingredient.unit.name,
              value: i.ingredient.id,
              label: i.ingredient.name,
            },
          })) || [],
        supplier: {
          label: resp.data.supplier?.name || '',
          value: resp.data.supplier?.id || null,
          isConnectedWithFoodInventory: resp.data.supplier?.isConnectedWithFoodInventory || false,
        },
      }
      return values
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMealSuppliers = createAsyncThunk(
  'food/getAllSuppliers',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IMealSupplierSingleResponse>> =
        await baseUrl.get('food/management/meal/supplier', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodEligibleEmployees = createAsyncThunk(
  'food/getFoodEligibleEmployees',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllFoodEligibleEmployeesSingleResponse>> =
        await baseUrl.get('food/management/users', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMealIngredients = createAsyncThunk(
  'food/getAllIngredients',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IIngredientSingleResponse>> = await baseUrl.get(
        'food/management/ingredient',
        {
          params,
        },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodDietaryTags = createAsyncThunk(
  'food/getFoodDietaryTags',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodDietaryTagResponse>> = await baseUrl.get(
        'food/management/dietary-tag',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodDietaryTag = createAsyncThunk(
  'food/createFoodDietaryTag',
  async (body: ICreateFoodDietaryTagRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodDietaryTagResponse> = await baseUrl.post(
        'food/management/dietary-tag',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFoodDietaryTag = createAsyncThunk(
  'food/updateFoodDietaryTag',
  async ({ code, body }: { code: string; body: IUpdateFoodDietaryTagRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodDietaryTagResponse> = await baseUrl.patch(
        'food/management/dietary-tag/' + code,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const archiveFoodDietaryTag = createAsyncThunk(
  'food/archiveFoodDietaryTag',
  async (code: string, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/dietary-tag/' + code)
      return code
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodKitchens = createAsyncThunk(
  'food/getFoodKitchens',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodKitchenResponse>> = await baseUrl.get(
        'food/management/kitchen',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodKitchen = createAsyncThunk(
  'food/createFoodKitchen',
  async (body: ICreateFoodKitchenRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodKitchenResponse> = await baseUrl.post(
        'food/management/kitchen',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFoodKitchen = createAsyncThunk(
  'food/updateFoodKitchen',
  async ({ id, body }: { id: number; body: IUpdateFoodKitchenRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodKitchenResponse> = await baseUrl.patch(
        'food/management/kitchen/' + id,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const archiveFoodKitchen = createAsyncThunk(
  'food/archiveFoodKitchen',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/kitchen/' + id)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodMealWindows = createAsyncThunk(
  'food/getFoodMealWindows',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodMealWindowResponse>> = await baseUrl.get(
        'food/management/meal-window',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodMealWindow = createAsyncThunk(
  'food/createFoodMealWindow',
  async (body: ICreateFoodMealWindowRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodMealWindowResponse> = await baseUrl.post(
        'food/management/meal-window',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFoodMealWindow = createAsyncThunk(
  'food/updateFoodMealWindow',
  async ({ id, body }: { id: number; body: IUpdateFoodMealWindowRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodMealWindowResponse> = await baseUrl.patch(
        'food/management/meal-window/' + id,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFoodMealWindow = createAsyncThunk(
  'food/deleteFoodMealWindow',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/meal-window/' + id)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodCatchmentRules = createAsyncThunk(
  'food/getFoodCatchmentRules',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodCatchmentRuleResponse>> = await baseUrl.get(
        'food/management/catchment-rule',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodCatchmentRule = createAsyncThunk(
  'food/createFoodCatchmentRule',
  async (body: ICreateFoodCatchmentRuleRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodCatchmentRuleResponse> = await baseUrl.post(
        'food/management/catchment-rule',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateFoodCatchmentRule = createAsyncThunk(
  'food/updateFoodCatchmentRule',
  async ({ id, body }: { id: number; body: IUpdateFoodCatchmentRuleRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodCatchmentRuleResponse> = await baseUrl.patch(
        'food/management/catchment-rule/' + id,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFoodCatchmentRule = createAsyncThunk(
  'food/deleteFoodCatchmentRule',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/management/catchment-rule/' + id)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodEmployeeProfiles = createAsyncThunk(
  'food/getFoodEmployeeProfiles',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IEmployeeFoodProfile>> = await baseUrl.get(
        'food/management/profile',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodEmployeeProfile = createAsyncThunk(
  'food/getFoodEmployeeProfile',
  async (userId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IEmployeeFoodProfile> = await baseUrl.get(
        'food/management/profile/' + userId,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const upsertFoodEmployeeProfile = createAsyncThunk(
  'food/upsertFoodEmployeeProfile',
  async (
    { userId, body }: { userId: number; body: IUpsertEmployeeFoodProfileRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IEmployeeFoodProfile> = await baseUrl.put(
        'food/management/profile/' + userId,
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodConfigVersions = createAsyncThunk(
  'food/getFoodConfigVersions',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodConfigVersion>> = await baseUrl.get(
        'food/management/config/versions',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const upsertFoodSitePolicy = createAsyncThunk(
  'food/upsertFoodSitePolicy',
  async (body: IUpsertFoodSitePolicyRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodConfigVersion> = await baseUrl.put(
        'food/management/config/site-policy',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const generateFoodIntents = createAsyncThunk(
  'food/generateFoodIntents',
  async (body: IGenerateFoodIntentsRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGenerateFoodIntentsResponse> = await baseUrl.post(
        'food/management/intents/generate',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodIntentsPreview = createAsyncThunk(
  'food/getFoodIntentsPreview',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<INextDayPreviewRow[]> = await baseUrl.get(
        'food/management/intents/preview',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const runFoodWindowCutoff = createAsyncThunk(
  'food/runFoodWindowCutoff',
  async (body: IFoodCutoffRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodQueuedJobResponse> = await baseUrl.post(
        'food/management/windows/cutoff',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const reconcileFoodAttendance = createAsyncThunk(
  'food/reconcileFoodAttendance',
  async (body: IFoodAttendanceReconcileRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodQueuedJobResponse> = await baseUrl.post(
        'food/management/attendance/reconcile',
        body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const recomputeFoodCoefficients = createAsyncThunk(
  'food/recomputeFoodCoefficients',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodQueuedJobResponse> = await baseUrl.post(
        'food/management/coefficients/recompute',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodCategories = createAsyncThunk(
  'food/getAllCategories',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodCategorySingleResponse>> =
        await baseUrl.get('food/inventory/category', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const generateRecords = createAsyncThunk(
  'food/generateRecords',
  async (date: Date, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post(
        'food/management/meal/provisioning-records/generate',
        { date: date.toISOString() },
      )
      await thunkAPI.dispatch(getDishes(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodCompanySettings = createAsyncThunk(
  'food/getFoodCompanySettings',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetFoodCompanySettingsResponse>> =
        await baseUrl.get('food/management/company/settings', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addFoodCompanySettings = createAsyncThunk(
  'food/addFoodCompanySettings',
  async (state: IFoodCompanySettingsModalState, thunkAPI) => {
    try {
      const req: IAddCompanyFoodSettingsRequest = {
        maxMealsPerOperationalDay: state.maxMealsPerOperationalDay,
        mealTypePriorities: state.mealTypePriorities.map((mtp) => ({
          mealTypeId: mtp.mealType.value,
          priority: mtp.priority,
        })),
      }
      const resp: AxiosResponse<IPaginatedResponse<IGetFoodCompanySettingsResponse>> =
        await baseUrl.post('food/management/company/settings', req)
      await thunkAPI.dispatch(getFoodCompanySettings(getParams({ location })))

      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addFoodAdHocProvisioning = createAsyncThunk(
  'food/addFoodAdHocProvisioning',
  async (state: IAdHocFoodState, thunkAPI) => {
    try {
      const req: IAddAdHocFoodProvisioningRequest = {
        date: state.date.toISOString(),
        mealTypes: state.mealTypes.map((mt) => ({
          mealTypeId: mt.mealType.value,
          count: mt.count,
          dietaryPreferenceType: mt.dietaryPreferenceType.value,
          foodSupplierId: mt.foodSupplier.value,
          provisioningMode: mt.provisioningMode.value,
        })),
      }
      const resp = await baseUrl.post('food/management/meal/provisioning-records/ad-hoc', req)

      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addFoodStandByProvisioning = createAsyncThunk(
  'food/addFoodStandByProvisioning',
  async (state: IStandByFoodProvisioningState, thunkAPI) => {
    try {
      const req: IAddStandByProvisioningRequest = {
        date: state.date.toISOString(),
        mealTypes: state.mealTypes.map((mt) => ({
          mealTypeId: mt.mealType.value,
          count: mt.count,
          dietaryPreferenceType: mt.dietaryPreferenceType.value,
          foodSupplierId: mt.foodSupplier.value,
        })),
      }
      const resp = await baseUrl.post('/v1/food/management/meal/provisioning-records/standby', req)

      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addFoodEmployeeSettings = createAsyncThunk(
  'food/addFoodEmployeeSettings',
  async (state: IFoodEmployeeSettingsModalState, thunkAPI) => {
    try {
      const req: IAddFoodEmployeeSettingsRequest = {
        provisioningMode: state.provisioningMode.value,
        foodSupplierId: state.foodSupplier.value,
        maxMealsPerOperationalDay: state.maxMealsPerOperationalDay,
        selectedMealTypeIds: state.selectedMealTypes.map((mt) => mt.value),
      }
      const resp = await baseUrl.post(`food/management/user/${state.id}/settings`, req)
      await thunkAPI.dispatch(getFoodEligibleEmployees(getParams({ location })))

      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodCategory = createAsyncThunk(
  'food/createCategory',
  async (state: { name: string; imageFile: File }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodCategorySingleResponse> = await baseUrl.post(
        'food/inventory/category',
        { name: state.name },
      )
      let respImg: AxiosResponse<IFoodCategorySingleResponse>
      if (state.imageFile) {
        const formData = new FormData()
        formData.append('image', state.imageFile)
        respImg = await baseUrl.post('food/inventory/category/' + resp.data.id + '/image', formData)
      }
      await thunkAPI.dispatch(getFoodCategories(getParams({ location })))

      return { ...resp.data, imageUrl: respImg?.data?.imageUrl || resp.data.imageUrl }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editFoodCategory = createAsyncThunk(
  'food/editCategory',
  async (state: { name: string; id: number; imageFile: File }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFoodCategorySingleResponse> = await baseUrl.patch(
        'food/inventory/category/' + state.id,
        { name: state.name },
      )
      let respImg: AxiosResponse<IFoodCategorySingleResponse>

      if (state.imageFile) {
        const formData = new FormData()
        formData.append('image', state.imageFile)
        respImg = await baseUrl.post('food/inventory/category/' + state.id + '/image', formData)
      }
      return { ...resp.data, imageUrl: respImg?.data?.imageUrl || resp.data.imageUrl }
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFoodCategory = createAsyncThunk(
  'food/deleteCategory',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/inventory/category/' + id)

      await thunkAPI.dispatch(getFoodCategories(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getFoodInventoryItems = createAsyncThunk(
  'food/getAllItems',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IFoodInventoyItemSingleResponse>> =
        await baseUrl.get('food/inventory/item', {
          params,
        })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createFoodInventoryItem = createAsyncThunk(
  'food/createItem',
  async (state: IAddFoodItemModalState, thunkAPI) => {
    try {
      const req: ICreateUpdateFoodInventoryItemRequest = {
        ingredientId: state.ingredient.value,
        categoryId: state.category.value,
        name: state.name,
        amount: state.amount,
        price: state.price,
      }
      const resp: AxiosResponse<IDishSingleResponse> = await baseUrl.post(
        'food/inventory/item',
        req,
      )
      await thunkAPI.dispatch(getFoodInventoryItems(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editFoodInventoryItem = createAsyncThunk(
  'food/editItem',
  async (state: IAddFoodItemModalState, thunkAPI) => {
    try {
      const req: ICreateUpdateFoodInventoryItemRequest = {
        ingredientId: state.ingredient.value,
        categoryId: state.category.value,
        name: state.name,
        amount: state.amount,
        price: state.price,
      }
      const resp: AxiosResponse<IDishSingleResponse> = await baseUrl.patch(
        'food/inventory/item/' + state.id,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteFoodInventoryItem = createAsyncThunk(
  'food/deleteItem',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('food/inventory/item/' + id)

      await thunkAPI.dispatch(getFoodInventoryItems(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSelectedDishes = createAsyncThunk('food/getSelectedDishes', async (_, thunkAPI) => {
  try {
    const resp: AxiosResponse<IGetSelectedDishesReponse> = await baseUrl.get(
      'food/management/company/selected-dishes',
    )
    return resp.data
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const addSelectedDish = createAsyncThunk(
  'food/addSelectedDish',
  async (dish: ISelectedDish[], thunkAPI) => {
    try {
      const req: IPostSelectedDishRequest = {
        selectedFoodDishes: dish.map((d) => ({
          dayInWeek: d.day.value,
          dietaryPreferenceType: d.dietaryPreference.value,
          mealTypeId: d.mealType.value,
          dishId: d.dish.value,
          supplierId: d.supplier.value,
        })),
      }
      await baseUrl.post('food/management/company/selected-dishes', req)

      await thunkAPI.dispatch(getSelectedDishes())
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const foodSlice = createSlice({
  name: 'food',
  initialState,
  reducers: {
    clearSingleDish: (state) => {
      state.dishes.singleDish = null
    },
    clearFood: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state = initialState
    },
    clearFoodReport: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state.foodReport = null
    },
    clearFoodCategories: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state.categories.data = []
    },
    clearFoodInventoryItems: (state) => {
      state.categories.data = []
    },
    clearSelectedDishes: (state) => {
      state.selectedDishes = null
    },
    clearFoodCompanySettings: (state) => {
      state.companySettings = null
    },
    clearEligibleEmployees: (state) => {
      state.eligibleEmployees = {
        data: [],
        totalPages: 0,
        totalCount: 0,
      }
    },
    clearProvisioningRecords: (state) => {
      state.provisioningRecords = {
        data: [],
        totalPages: 0,
        totalCount: 0,
      }
    },
    clearSelectedEmployeeFoodProfile: (state) => {
      state.selectedEmployeeFoodProfile = null
    },
    clearFoodIntentsPreview: (state) => {
      state.nextDayPreview = []
    },
  },
  extraReducers: {
    [getProvisioningRecords.pending.type]: (state) => {
      state.isLoading = true
    },
    [getProvisioningRecords.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllFoodProvisioningRecordsSingleResponse> },
    ) => {
      state.provisioningRecords = payload
      state.isLoading = false
    },
    [getProvisioningRecords.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [generateRecords.pending.type]: (state) => {
      state.isLoading = true
    },
    [generateRecords.fulfilled.type]: (state) => {
      toast.success('Provisioning records generated successfully')
      state.isLoading = false
    },
    [generateRecords.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addFoodAdHocProvisioning.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Ad-hoc provisioning added successfully')
    },
    [addFoodAdHocProvisioning.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addFoodAdHocProvisioning.pending.type]: (state) => {
      state.isLoading = true
    },
    [addFoodAdHocProvisioning.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Ad-hoc provisioning added successfully')
    },
    [addFoodAdHocProvisioning.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodCompanySettings.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodCompanySettings.fulfilled.type]: (state, { payload }) => {
      state.companySettings = payload
      state.isLoading = false
    },
    [getFoodCompanySettings.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodEligibleEmployees.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodEligibleEmployees.fulfilled.type]: (state, { payload }) => {
      state.eligibleEmployees = payload
      state.isLoading = false
    },
    [getFoodEligibleEmployees.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addFoodEmployeeSettings.pending.type]: (state) => {
      state.isLoading = true
    },
    [addFoodEmployeeSettings.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Settings added successfully')
    },
    [addFoodEmployeeSettings.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createMealType.pending.type]: (state) => {
      state.isLoading = true
    },
    [createMealType.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Meal type created successfully')
    },
    [createMealType.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSelectedDishes.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSelectedDishes.fulfilled.type]: (
      state,
      { payload }: { payload: IGetSelectedDishesReponse },
    ) => {
      state.isLoading = false
      state.selectedDishes = payload
    },
    [getSelectedDishes.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addSelectedDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [addSelectedDish.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Dish added successfully')
    },
    [addSelectedDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodReport.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodReport.fulfilled.type]: (state, { payload }: { payload: IGetFoodReportResponse }) => {
      state.isLoading = false
      state.oldFoodReport = payload.report
    },
    [getFoodReport.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [confirmFoodReport.pending.type]: (state) => {
      state.isLoading = true
    },
    [confirmFoodReport.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Food report confirmed successfully')
    },
    [confirmFoodReport.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [generateFoodReport.pending.type]: (state) => {
      state.isLoading = true
    },
    [generateFoodReport.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.foodReport = payload
      toast.success('Food report generated successfully')
    },
    [generateFoodReport.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editFoodReport.pending.type]: (state) => {
      state.isLoading = true
    },
    [editFoodReport.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Food report edited generated')
    },
    [editFoodReport.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editMealType.pending.type]: (state) => {
      state.isLoading = true
    },
    [editMealType.fulfilled.type]: (
      state,
      { payload }: { payload: IGetAllMealTypesSingleResponse },
    ) => {
      state.isLoading = false
      toast.success('Meal type updated successfully')
      state.mealTypes.data = state.mealTypes.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
    },
    [editMealType.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteMealType.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteMealType.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Meal type deleted successfully')
    },
    [deleteMealType.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createMealSupplier.pending.type]: (state) => {
      state.isLoading = true
    },
    [createMealSupplier.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Meal supplier created successfully')
    },
    [createMealSupplier.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editMealSupplier.pending.type]: (state) => {
      state.isLoading = true
    },
    [editMealSupplier.fulfilled.type]: (
      state,
      { payload }: { payload: IMealSupplierSingleResponse },
    ) => {
      state.isLoading = false
      toast.success('Meal supplier updated successfully')
      state.mealSuppliers.data = state.mealSuppliers.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
    },
    [editMealSupplier.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteMealSupplier.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteMealSupplier.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Meal supplier deleted successfully')
    },
    [deleteMealSupplier.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createIngredient.pending.type]: (state) => {
      state.isLoading = true
    },
    [createIngredient.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Ingredient created successfully')
    },
    [createIngredient.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editIngredient.pending.type]: (state) => {
      state.isLoading = true
    },
    [editIngredient.fulfilled.type]: (
      state,
      { payload }: { payload: IIngredientSingleResponse },
    ) => {
      state.isLoading = false
      toast.success('Ingredient updated successfully')
      state.mealIngredients.data = state.mealIngredients.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
    },
    [editIngredient.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteIngredient.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteIngredient.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Ingredient deleted successfully')
    },
    [deleteIngredient.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [createDish.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Dish created successfully')
    },
    [createDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [editDish.fulfilled.type]: (state, { payload }: { payload: IDishSingleResponse }) => {
      state.isLoading = false
      toast.success('Dish updated successfully')
      state.dishes.data = state.dishes.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
    },
    [editDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteDish.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Dish deleted successfully')
    },
    [deleteDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getDishes.pending.type]: (state) => {
      state.isLoading = true
    },
    [getDishes.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllDishesSingleResponse> },
    ) => {
      state.isLoading = false
      state.dishes.data = payload.data
      state.dishes.totalCount = payload.totalCount
      state.dishes.totalPages = payload.totalPages
    },
    [getDishes.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleDish.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleDish.fulfilled.type]: (state, { payload }: { payload: IDishesModal }) => {
      state.isLoading = false
      state.dishes.singleDish = payload
    },
    [getSingleDish.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getMealTypes.pending.type]: (state) => {
      state.isLoading = true
    },
    [getMealTypes.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllMealTypesSingleResponse> },
    ) => {
      state.isLoading = false
      state.mealTypes.data = payload.data
      state.mealTypes.totalCount = payload.totalCount
      state.mealTypes.totalPages = payload.totalPages
    },
    [getMealTypes.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getMealSuppliers.pending.type]: (state) => {
      state.isLoading = true
    },
    [getMealSuppliers.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IMealSupplierSingleResponse> },
    ) => {
      state.isLoading = false
      state.mealSuppliers.data = payload.data
      state.mealSuppliers.totalCount = payload.totalCount
      state.mealSuppliers.totalPages = payload.totalPages
    },
    [getMealSuppliers.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getMealIngredients.pending.type]: (state) => {
      state.isLoading = true
    },
    [getMealIngredients.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IIngredientSingleResponse> },
    ) => {
      state.isLoading = false
      state.mealIngredients.data = payload.data
      state.mealIngredients.totalCount = payload.totalCount
      state.mealIngredients.totalPages = payload.totalPages
    },
    [getMealIngredients.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodCategories.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodCategories.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodCategorySingleResponse> },
    ) => {
      state.isLoading = false
      state.categories.data = payload.data
      state.categories.totalCount = payload.totalCount
      state.categories.totalPages = payload.totalPages
    },
    [getFoodCategories.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodCategory.pending.type]: (state) => {
      state.isLoading = true
    },
    [createFoodCategory.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Food category updated successfully')
    },
    [createFoodCategory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editFoodCategory.pending.type]: (state) => {
      state.isLoading = true
    },
    [editFoodCategory.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodCategorySingleResponse },
    ) => {
      state.isLoading = false
      state.categories.data = state.categories.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
      toast.success('Food category updated successfully')
    },
    [editFoodCategory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteFoodCategory.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteFoodCategory.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Food category deleted successfully')
    },
    [deleteFoodCategory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodInventoryItems.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodInventoryItems.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodInventoyItemSingleResponse> },
    ) => {
      state.isLoading = false
      state.items.data = payload.data
      state.items.totalCount = payload.totalCount
      state.items.totalPages = payload.totalPages
    },
    [getFoodInventoryItems.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteFoodInventoryItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteFoodInventoryItem.fulfilled.type]: (state, { payload }: { payload: number }) => {
      state.isLoading = false
      state.items.data = state.items.data.filter((d) => d.id !== payload)
      toast.success('Food item deleted successfully')
    },
    [deleteFoodInventoryItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodInventoryItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [createFoodInventoryItem.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Food item created successfully')
    },
    [createFoodInventoryItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editFoodInventoryItem.pending.type]: (state) => {
      state.isLoading = true
    },
    [editFoodInventoryItem.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodInventoyItemSingleResponse },
    ) => {
      state.isLoading = false
      state.items.data = state.items.data.map((d) => {
        if (d.id !== payload.id) {
          return d
        } else {
          return payload
        }
      })
      toast.success('Food item updated successfully')
    },
    [editFoodInventoryItem.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getFoodDietaryTags.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodDietaryTags.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodDietaryTagResponse> },
    ) => {
      state.isLoading = false
      state.dietaryTags = payload
    },
    [getFoodDietaryTags.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodDietaryTag.fulfilled.type]: (state, { payload }: { payload: IFoodDietaryTagResponse }) => {
      state.dietaryTags.data = [payload, ...state.dietaryTags.data]
      toast.success('Dietary tag created successfully')
    },
    [createFoodDietaryTag.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [updateFoodDietaryTag.fulfilled.type]: (state, { payload }: { payload: IFoodDietaryTagResponse }) => {
      state.dietaryTags.data = state.dietaryTags.data.map((row) =>
        row.code === payload.code ? payload : row,
      )
      toast.success('Dietary tag updated successfully')
    },
    [updateFoodDietaryTag.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [archiveFoodDietaryTag.fulfilled.type]: (state, { payload }: { payload: string }) => {
      state.dietaryTags.data = state.dietaryTags.data.filter((row) => row.code !== payload)
      toast.success('Dietary tag archived successfully')
    },
    [archiveFoodDietaryTag.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodKitchens.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodKitchens.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodKitchenResponse> },
    ) => {
      state.isLoading = false
      state.logisticsKitchens = payload
    },
    [getFoodKitchens.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodKitchen.fulfilled.type]: (state, { payload }: { payload: IFoodKitchenResponse }) => {
      state.logisticsKitchens.data = [payload, ...state.logisticsKitchens.data]
      toast.success('Kitchen created successfully')
    },
    [createFoodKitchen.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [updateFoodKitchen.fulfilled.type]: (state, { payload }: { payload: IFoodKitchenResponse }) => {
      state.logisticsKitchens.data = state.logisticsKitchens.data.map((row) =>
        row.id === payload.id ? payload : row,
      )
      toast.success('Kitchen updated successfully')
    },
    [updateFoodKitchen.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [archiveFoodKitchen.fulfilled.type]: (state, { payload }: { payload: number }) => {
      state.logisticsKitchens.data = state.logisticsKitchens.data.filter((row) => row.id !== payload)
      toast.success('Kitchen archived successfully')
    },
    [archiveFoodKitchen.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodMealWindows.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodMealWindows.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodMealWindowResponse> },
    ) => {
      state.isLoading = false
      state.mealWindows = payload
    },
    [getFoodMealWindows.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodMealWindow.fulfilled.type]: (state, { payload }: { payload: IFoodMealWindowResponse }) => {
      state.mealWindows.data = [payload, ...state.mealWindows.data]
      toast.success('Meal window created successfully')
    },
    [createFoodMealWindow.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [updateFoodMealWindow.fulfilled.type]: (state, { payload }: { payload: IFoodMealWindowResponse }) => {
      state.mealWindows.data = state.mealWindows.data.map((row) =>
        row.id === payload.id ? payload : row,
      )
      toast.success('Meal window updated successfully')
    },
    [updateFoodMealWindow.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [deleteFoodMealWindow.fulfilled.type]: (state, { payload }: { payload: number }) => {
      state.mealWindows.data = state.mealWindows.data.filter((row) => row.id !== payload)
      toast.success('Meal window deleted successfully')
    },
    [deleteFoodMealWindow.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodCatchmentRules.pending.type]: (state) => {
      state.isLoading = true
    },
    [getFoodCatchmentRules.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodCatchmentRuleResponse> },
    ) => {
      state.isLoading = false
      state.catchmentRules = payload
    },
    [getFoodCatchmentRules.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createFoodCatchmentRule.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodCatchmentRuleResponse },
    ) => {
      state.catchmentRules.data = [payload, ...state.catchmentRules.data]
      toast.success('Catchment rule created successfully')
    },
    [createFoodCatchmentRule.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [updateFoodCatchmentRule.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodCatchmentRuleResponse },
    ) => {
      state.catchmentRules.data = state.catchmentRules.data.map((row) =>
        row.id === payload.id ? payload : row,
      )
      toast.success('Catchment rule updated successfully')
    },
    [updateFoodCatchmentRule.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [deleteFoodCatchmentRule.fulfilled.type]: (state, { payload }: { payload: number }) => {
      state.catchmentRules.data = state.catchmentRules.data.filter((row) => row.id !== payload)
      toast.success('Catchment rule deleted successfully')
    },
    [deleteFoodCatchmentRule.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodEmployeeProfiles.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IEmployeeFoodProfile> },
    ) => {
      state.employeeFoodProfiles = payload
    },
    [getFoodEmployeeProfiles.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodEmployeeProfile.fulfilled.type]: (state, { payload }: { payload: IEmployeeFoodProfile }) => {
      state.selectedEmployeeFoodProfile = payload
    },
    [getFoodEmployeeProfile.rejected.type]: (state, { payload }) => {
      state.selectedEmployeeFoodProfile = null
      handleErrors(payload)
    },
    [upsertFoodEmployeeProfile.fulfilled.type]: (
      state,
      { payload }: { payload: IEmployeeFoodProfile },
    ) => {
      state.selectedEmployeeFoodProfile = payload
      const exists = state.employeeFoodProfiles.data.some((row) => row.userId === payload.userId)
      state.employeeFoodProfiles.data = exists
        ? state.employeeFoodProfiles.data.map((row) =>
            row.userId === payload.userId ? payload : row,
          )
        : [payload, ...state.employeeFoodProfiles.data]
      toast.success('Food profile saved')
    },
    [upsertFoodEmployeeProfile.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodConfigVersions.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFoodConfigVersion> },
    ) => {
      state.configVersions = payload
    },
    [getFoodConfigVersions.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [upsertFoodSitePolicy.fulfilled.type]: (state, { payload }: { payload: IFoodConfigVersion }) => {
      state.configVersions.data = [payload, ...state.configVersions.data]
      toast.success('Site policy saved — effective next operational day')
    },
    [upsertFoodSitePolicy.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [generateFoodIntents.fulfilled.type]: (
      state,
      { payload }: { payload: IGenerateFoodIntentsResponse },
    ) => {
      toast.success(payload?.message || 'Intent generation has been queued.')
    },
    [generateFoodIntents.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [runFoodWindowCutoff.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodQueuedJobResponse },
    ) => {
      toast.success(payload?.message || 'Cut-off run has been queued.')
    },
    [runFoodWindowCutoff.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [reconcileFoodAttendance.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodQueuedJobResponse },
    ) => {
      toast.success(payload?.message || 'Attendance reconciliation has been queued.')
    },
    [reconcileFoodAttendance.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [recomputeFoodCoefficients.fulfilled.type]: (
      state,
      { payload }: { payload: IFoodQueuedJobResponse },
    ) => {
      toast.success(payload?.message || 'Show-rate coefficient recompute has been queued.')
    },
    [recomputeFoodCoefficients.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
    [getFoodIntentsPreview.fulfilled.type]: (
      state,
      { payload }: { payload: INextDayPreviewRow[] },
    ) => {
      state.nextDayPreview = payload || []
    },
    [getFoodIntentsPreview.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
    },
  },
})

export const {
  clearFood,
  clearSingleDish,
  clearFoodCategories,
  clearFoodInventoryItems,
  clearFoodReport,
  clearSelectedDishes,
  clearFoodCompanySettings,
  clearEligibleEmployees,
  clearProvisioningRecords,
  clearSelectedEmployeeFoodProfile,
  clearFoodIntentsPreview,
} = foodSlice.actions
export default foodSlice.reducer
